import { useRef, useEffect, useMemo } from "react"
import { useGLTF } from "@react-three/drei"
import * as THREE from "three"
import type { ShelvingDimensions } from "@/lib/types"
import { CM_TO_M, calculateBracingCount, calculateBracingPositions } from "@/lib/utils"
import { useShelvingStore } from "@/lib/shelving-store"

function disposeObject(obj: THREE.Object3D) {
  obj.traverse((node) => {
    const mesh = node as THREE.Mesh
    if (mesh.isMesh) {
      mesh.geometry?.dispose()
      if (Array.isArray(mesh.material)) {
        mesh.material.forEach((m) => m.dispose())
      } else {
        (mesh.material as THREE.Material)?.dispose()
      }
    }
  })
}

// Base URL for all models
const MODEL_BASE = "/models/"
const USE_SAFE_PRELOAD = false

function getFrameModelPath(type: "open" | "closed" | "outside", heightCm: number, depthCm: number) {
  const baseHeightMm = 2000
  const baseDepthMm = 400
  return `${MODEL_BASE}frame_${baseHeightMm}x${baseDepthMm}_${type}.glb`
}

function getShelfModelPath(widthCm: number, depthCm: number, shelfType: string): string {
  const w = Math.round(widthCm * 10)
  const baseDepth = 400
  const type = shelfType === "L2505" ? "l" : "m"
  return `${MODEL_BASE}shelf_${w}x${baseDepth}_${type}.glb`
}

function maybeEnableShadows(object: THREE.Object3D, enableShadows: boolean) {
  if (!enableShadows) return
  object.traverse((node) => {
    if ((node as THREE.Mesh).isMesh) {
      node.castShadow = true
      node.receiveShadow = true
      const mesh = node as THREE.Mesh
      if (mesh.material) {
        if (Array.isArray(mesh.material)) {
          mesh.material.forEach((mat) => {
            if (mat instanceof THREE.MeshStandardMaterial) {
              mat.roughness = 0.7
              mat.metalness = 0.1
            }
          })
        } else if (mesh.material instanceof THREE.MeshStandardMaterial) {
          mesh.material.roughness = 0.7
          mesh.material.metalness = 0.1
        }
      }
    }
  })
}

// Optimize loaded models for performance
function optimizeModel(object: THREE.Object3D) {
  object.traverse((node) => {
    if ((node as THREE.Mesh).isMesh) {
      const mesh = node as THREE.Mesh

      // Optimize geometry
      if (mesh.geometry) {
        mesh.geometry.computeBoundingSphere()
        mesh.geometry.computeBoundingBox()
      }

      // Optimize materials
      if (mesh.material) {
        if (Array.isArray(mesh.material)) {
          mesh.material.forEach((mat) => {
            if (mat instanceof THREE.MeshStandardMaterial) {
              mat.roughness = 0.7
              mat.metalness = 0.1
              mat.needsUpdate = false
            }
          })
        } else if (mesh.material instanceof THREE.MeshStandardMaterial) {
          mesh.material.roughness = 0.7
          mesh.material.metalness = 0.1
          mesh.material.needsUpdate = false
        }
      }

      // Enable frustum culling
      mesh.frustumCulled = true
    }
  })
}

export function ShelvingModelLoader(
  props: ShelvingDimensions & { showShadows?: boolean }
) {
  const { model, height, width, depth, shelfCount, sectionCount, frameState, sides, showShadows = false } = props
  const { shelfLoadPerShelf } = useShelvingStore()
  const groupRef = useRef<THREE.Group>(null)
  const clonedObjectsRef = useRef<THREE.Object3D[]>([])

  useEffect(() => {
    preloadAllModels()
  }, [])

  let openFrame, closedFrame, shelf, verticalBracingSingle, verticalBracingDouble
  let hasError = false

  const openFramePath = getFrameModelPath("open", height, depth)
  const closedFramePath = getFrameModelPath("closed", height, depth)

  try {
    if (openFramePath) {
      openFrame = useGLTF(openFramePath)
    }
  } catch (e) {
    hasError = true
  }

  try {
    if (closedFramePath) {
      closedFrame = useGLTF(closedFramePath)
    }
  } catch (e) {
    hasError = true
  }

  try {
    shelf = useGLTF(getShelfModelPath(width, depth, model))
  } catch (e) {
    hasError = true
  }

  try {
    verticalBracingSingle = useGLTF(`${MODEL_BASE}vb_single_2000x1000.glb`)
  } catch (e) {
    hasError = true
  }

  try {
    verticalBracingDouble = useGLTF(`${MODEL_BASE}vb_double_2000x1000.glb`)
  } catch (e) {
    hasError = true
  }

  if (openFrame?.scene) {
    maybeEnableShadows(openFrame.scene, showShadows)
    optimizeModel(openFrame.scene)
  }
  if (closedFrame?.scene) {
    maybeEnableShadows(closedFrame.scene, showShadows)
    optimizeModel(closedFrame.scene)
  }
  if (shelf?.scene) {
    maybeEnableShadows(shelf.scene, showShadows)
    optimizeModel(shelf.scene)
  }
  if (verticalBracingSingle?.scene) {
    maybeEnableShadows(verticalBracingSingle.scene, showShadows)
    optimizeModel(verticalBracingSingle.scene)
  }
  if (verticalBracingDouble?.scene) {
    maybeEnableShadows(verticalBracingDouble.scene, showShadows)
    optimizeModel(verticalBracingDouble.scene)
  }

  // Dispose all previously tracked clones on unmount
  useEffect(() => {
    return () => {
      clonedObjectsRef.current.forEach(disposeObject)
      clonedObjectsRef.current = []
    }
  }, [])

  const trackClone = (scene: THREE.Object3D): THREE.Object3D => {
    const clone = scene.clone(true)
    clonedObjectsRef.current.push(clone)
    return clone
  }

  const FRAME_WIDTH = 3
  const modelScale = 1
  const heightMetres = height * CM_TO_M
  const marginBottom = 4 * CM_TO_M
  const marginTop = 2 * CM_TO_M
  const shelfHeightL = 0.025
  const shelfHeightM = 0.040
  const shelfHeight = model === "M4006" ? shelfHeightM : shelfHeightL
  const shelfYOffset = shelfHeight - 0.015

  const shelfPositions = useMemo(
    () =>
      shelfCount > 1
        ? Array.from({ length: shelfCount }, (_, i) =>
            marginBottom + ((heightMetres - marginBottom - marginTop) / (shelfCount - 1)) * i
          )
        : [heightMetres / 2],
    [shelfCount, heightMetres, marginBottom, marginTop]
  )

  const getFrameSceneForIndex = (index: number, total: number): THREE.Object3D | null => {
    if (frameState === "outside") {
      if (index === 0 || index === total - 1) {
        return closedFrame?.scene ? trackClone(closedFrame.scene) : null
      }
      return openFrame?.scene ? trackClone(openFrame.scene) : null
    }
    const scene = frameState === "closed" ? closedFrame?.scene : openFrame?.scene
    return scene ? trackClone(scene) : null
  }

  const getVerticalBracingPositions = () => {
    const count = calculateBracingCount(sectionCount, sides, shelfLoadPerShelf, shelfCount)
    return calculateBracingPositions(count, sectionCount)
  }

  function renderRow(rowIndex: number, zOffset: number) {
    const sections = Array.from({ length: sectionCount + 1 }, (_, sectionIndex) => {
      const frameScene = getFrameSceneForIndex(sectionIndex, sectionCount + 1)
      const baseHeightCm = 200
      const baseDepthCm = 40
      const heightScaleFactor = height / baseHeightCm
      const depthScaleFactor = depth / baseDepthCm
      const frameScale = [
        modelScale * 0.001,
        -modelScale * 0.001 * depthScaleFactor,
        modelScale * 0.001 * heightScaleFactor
      ]
      const frameRotation = [Math.PI / 2, 0, Math.PI]

      return frameScene ? (
        <group
          key={`upright-${rowIndex}-${sectionIndex}`}
          position={[sectionIndex * (FRAME_WIDTH + width) * CM_TO_M + 0.5, 0, zOffset - 0.2]}
        >
          <primitive
            object={frameScene}
            scale={frameScale}
            rotation={frameRotation}
          />
        </group>
      ) : null
    })

    const baseDepthCm = 40
    const depthScaleFactor = depth / baseDepthCm

    const shelves = shelfPositions.flatMap((yPos, shelfIndex) =>
      Array.from({ length: sectionCount }, (_, sectionIndex) => (
        <group
          key={`shelf-${rowIndex}-${sectionIndex}-${shelfIndex}`}
          position={[
            sectionIndex * (FRAME_WIDTH + width) * CM_TO_M +
              ((FRAME_WIDTH + width) * CM_TO_M) / 2 +
              (width === 127 ? -15 * CM_TO_M : 0),
            yPos + shelfYOffset,
            zOffset - 0.2,
          ]}
        >
          {shelf?.scene && (
            <primitive
              object={trackClone(shelf.scene)}
              scale={[modelScale * 0.001, modelScale * 0.001 * depthScaleFactor, modelScale * 0.001]}
              rotation={[Math.PI / 2, 0, 0]}
            />
          )}
        </group>
      ))
    )

    return [...sections.filter(Boolean), ...shelves]
  }

  const renderVerticalBracing = () => {
    const bracingPositions = getVerticalBracingPositions()
    const zPosition = depth === 40 ? -0.4 : depth === 50 ? -0.5 : -0.6

    const verticalBracingModel = sides === "double" ? verticalBracingDouble : verticalBracingSingle

    const baseWidth = 1000
    const actualShelfWidth = width * 10
    const frameWidth = 30
    const totalWidth = actualShelfWidth + frameWidth
    const xScale = totalWidth / baseWidth

    return bracingPositions.map((sectionIndex) => {
      const xPosition =
        sectionIndex * (width + FRAME_WIDTH) * CM_TO_M +
        (FRAME_WIDTH * CM_TO_M) / 2 -
        1.5 * CM_TO_M +
        0.5

      return (
        <group key={`vertical-bracing-${sectionIndex}`} position={[xPosition, 0, zPosition - 0.2]}>
          {verticalBracingModel?.scene && (
            <primitive
              object={trackClone(verticalBracingModel.scene)}
              scale={[0.001 * xScale, 0.001, 0.001]}
              rotation={[0, 0, 0]}
            />
          )}
        </group>
      )
    })
  }

  // Dispose previous render's clones before building new ones each render
  clonedObjectsRef.current.forEach(disposeObject)
  clonedObjectsRef.current = []

  const rows = [
    renderRow(0, 0),
    ...(sides === "double"
      ? [
          <group
            key="mirrored-row"
            scale={[1, 1, -1]}
            position={[0, 0, -((depth * 2 * CM_TO_M) + 0.41)]}
          >
            {renderRow(1, 0)}
          </group>,
        ]
      : []),
    ...renderVerticalBracing(),
  ]

  return (
    <group ref={groupRef} frustumCulled={true} position={[-0.5, 0, 0.2]}>
      {rows.flat()}
    </group>
  )
}

// --- Preload Section ---

const possibleWidths = [97, 127]
const possibleDepths = [40]
const possibleTypes = ["l", "m"]

function preloadAllModels() {
  if (USE_SAFE_PRELOAD) return
  for (const width of possibleWidths) {
    for (const depth of possibleDepths) {
      for (const type of possibleTypes) {
        const w = Math.round(width * 10)
        const d = Math.round(depth * 10)
        useGLTF.preload(`${MODEL_BASE}shelf_${w}x${d}_${type}.glb`)
      }
    }
  }
  useGLTF.preload(`${MODEL_BASE}frame_2000x400_open.glb`)
  useGLTF.preload(`${MODEL_BASE}frame_2200x400_open.glb`)
  useGLTF.preload(`${MODEL_BASE}frame_2500x400_open.glb`)
  useGLTF.preload(`${MODEL_BASE}frame_2000x400_closed.glb`)
  useGLTF.preload(`${MODEL_BASE}vb_single_2000x1000.glb`)
  useGLTF.preload(`${MODEL_BASE}vb_double_2000x1000.glb`)
}
